"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.staticClassName = exports.avatarColoredFallbackClassName = exports.Avatar = void 0;
var _react = _interopRequireWildcard(require("react"));
require("./Avatar.css");
var _cdsCommon = require("@cbhq/cds-common");
var _useShapeToBorderRadiusAlias = require("@cbhq/cds-common/hooks/useShapeToBorderRadiusAlias");
var _useAvatarSize = require("@cbhq/cds-common/media/useAvatarSize");
var _useAvatarSrc = require("@cbhq/cds-common/media/useAvatarSrc");
var _avatar = require("@cbhq/cds-common/tokens/avatar");
var _interactableHeight = require("@cbhq/cds-common/tokens/interactableHeight");
var _useAccessibleForeground = require("../color/useAccessibleForeground");
var _usePaletteValueToRgbaString = require("../color/usePaletteValueToRgbaString");
var _layout = require("../layout");
var _typography = require("../typography");
var _linaria = require("../utils/linaria");
var _Hexagon = require("./Hexagon");
var _RemoteImage = require("./RemoteImage");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const staticClassName = 'cds-avatar';
exports.staticClassName = staticClassName;
const avatarColoredFallbackClassName = 'cds-avatar-colored-fallback';
exports.avatarColoredFallbackClassName = avatarColoredFallbackClassName;
const borderWidth = 2;
const borderStyles = "cds-borderStyles-b4k4xh5";
const Avatar = /*#__PURE__*/(0, _react.memo)(({
  alt,
  src,
  shape = 'circle',
  size = 'l',
  borderColor,
  className,
  testID,
  dangerouslySetSize,
  selected,
  colorScheme: colorSchemeProp,
  name
}) => {
  const borderRadius = (0, _useShapeToBorderRadiusAlias.useShapeToBorderRadiusAlias)(shape);
  const avatarSize = (0, _useAvatarSize.useAvatarSize)(size);
  const avatarSrc = (0, _useAvatarSrc.useAvatarSrc)(src);
  const spectrum = (0, _cdsCommon.useSpectrum)();
  const placeholderLetter = name === null || name === void 0 ? void 0 : name.charAt(0);
  const isLargestSize = size.includes('xx');
  const isCompactAvatarButton = dangerouslySetSize && dangerouslySetSize <= _interactableHeight.interactableHeight.xSmall.regular;
  const isNormalAvatarButton = dangerouslySetSize && dangerouslySetSize > _interactableHeight.interactableHeight.xSmall.regular;
  const colorScheme = (0, _react.useMemo)(() => _avatar.colorSchemeMap[spectrum][colorSchemeProp !== null && colorSchemeProp !== void 0 ? colorSchemeProp : 'blue'], [spectrum, colorSchemeProp]);
  const colorSchemeRgb = (0, _usePaletteValueToRgbaString.usePaletteValueToRgbaString)(colorScheme);
  const fallbackTextColor = (0, _useAccessibleForeground.useAccessibleForeground)({
    background: colorSchemeRgb,
    color: _avatar.avatarDefaultFontColor,
    usage: isLargestSize ? 'largeText' : 'normalText'
  });
  const computedSize = dangerouslySetSize !== null && dangerouslySetSize !== void 0 ? dangerouslySetSize : avatarSize;
  const shouldShowAvatarImage = !!src || !name;
  // only show a border for normal and fallback image treatments
  const hasBorder = shouldShowAvatarImage && borderColor && shape !== 'hexagon';
  const styleOverrides = (0, _react.useMemo)(() => ({
    background: colorSchemeRgb
  }), [colorSchemeRgb]);
  const ringStyles = (0, _react.useMemo)(() => ({
    boxShadow: `0 0 0 ${borderWidth}px ${colorSchemeRgb}`
  }), [colorSchemeRgb]);
  const boxOverflow = (0, _react.useMemo)(() => shape !== 'hexagon' ? 'hidden' : undefined, [shape]);
  const avatarText = (0, _react.useMemo)(() => {
    if (isLargestSize || isNormalAvatarButton) {
      return /*#__PURE__*/_react.default.createElement(_typography.TextTitle2, {
        align: "center",
        as: "p",
        dangerouslySetColor: fallbackTextColor,
        transform: "uppercase"
      }, placeholderLetter);
    }
    if (size === 'm' || isCompactAvatarButton) {
      return /*#__PURE__*/_react.default.createElement(_typography.TextCaption, {
        align: "center",
        as: "p",
        dangerouslySetColor: fallbackTextColor,
        transform: "uppercase"
      }, placeholderLetter);
    }
    return /*#__PURE__*/_react.default.createElement(_typography.TextBody, {
      align: "center",
      as: "p",
      dangerouslySetColor: fallbackTextColor,
      transform: "uppercase"
    }, placeholderLetter);
  }, [fallbackTextColor, isCompactAvatarButton, isLargestSize, isNormalAvatarButton, placeholderLetter, size]);
  const isHexagon = shape === 'hexagon';
  const hexagonClassName = (0, _react.useMemo)(() => {
    if (isHexagon) {
      return `${_Hexagon.hexagonStyles} ${_Hexagon.staticHexagonClassName}`;
    }
    return undefined;
  }, [isHexagon]);
  const coloredFallback = (0, _react.useMemo)(() => {
    return /*#__PURE__*/_react.default.createElement(_layout.Box, {
      alignItems: "center",
      borderRadius: borderRadius,
      className: avatarColoredFallbackClassName,
      flexGrow: 1,
      height: "100%",
      justifyContent: "center",
      style: styleOverrides,
      testID: `${testID}-fallback`
    }, avatarText);
  }, [avatarText, borderRadius, styleOverrides, testID]);
  const dangerouslySetBackground = (0, _react.useMemo)(() => {
    if (src) {
      return src;
    }
    if (!src && isHexagon) {
      return !colorSchemeProp ? 'transparent' : colorSchemeRgb;
    }
    return undefined;
  }, [colorSchemeProp, colorSchemeRgb, isHexagon, src]);
  return /*#__PURE__*/_react.default.createElement(_layout.Box, {
    position: "relative"
  }, /*#__PURE__*/_react.default.createElement(_layout.Box, {
    alignItems: "center",
    borderColor: hasBorder ? borderColor : 'transparent',
    borderRadius: borderRadius,
    className: (0, _linaria.cx)(staticClassName, className, hasBorder ? borderStyles : undefined, hexagonClassName),
    dangerouslySetBackground: dangerouslySetBackground,
    flexGrow: 0,
    flexShrink: 0,
    height: computedSize,
    justifyContent: "center",
    overflow: boxOverflow,
    position: "relative",
    style: selected && !isHexagon ? ringStyles : undefined,
    testID: testID,
    width: computedSize
  }, shouldShowAvatarImage ? /*#__PURE__*/_react.default.createElement(_RemoteImage.RemoteImage, {
    alt: alt,
    height: computedSize,
    shape: shape,
    source: avatarSrc,
    width: computedSize
  }) : coloredFallback), isHexagon && selected && /*#__PURE__*/_react.default.createElement(_Hexagon.HexagonBorder, {
    offset: true,
    size: size,
    strokeColor: colorSchemeRgb
  }), isHexagon && borderColor && /*#__PURE__*/_react.default.createElement(_Hexagon.HexagonBorder, {
    size: size,
    strokeColor: `var(--${borderColor})`
  }));
});
exports.Avatar = Avatar;